import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Search, 
  Filter,
  Clock,
  CheckCircle,
  AlertTriangle,
  Users,
  MapPin,
  Calendar,
  DollarSign,
  ArrowRight,
  Eye
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HoldingProjects() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");

  useEffect(() => {
    fetchProjects();
  }, [token]);

  useEffect(() => {
    filterProjects();
  }, [projects, searchQuery, statusFilter]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      
      // Fetch ALL projects for holding admin (includes all statuses)
      const res = await fetch(`${API_URL}/api/projects-v3/holding/all-projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error("Failed to fetch projects");
      
      const allProjects = await res.json();
      
      // Sort by created_at desc
      allProjects.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setProjects(allProjects);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Kunne ikke laste prosjekter");
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = [...projects];
    
    // Status filter
    if (statusFilter && statusFilter !== "all") {
      if (statusFilter === "pending") {
        filtered = filtered.filter(p => p.status === "pending_holding_review");
      } else if (statusFilter === "live") {
        filtered = filtered.filter(p => ["live", "has_applications", "has_counter_offers"].includes(p.status));
      } else if (statusFilter === "active") {
        filtered = filtered.filter(p => ["team_selected", "team_contracted", "in_progress"].includes(p.status));
      } else {
        filtered = filtered.filter(p => p.status === statusFilter);
      }
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.title?.toLowerCase().includes(query) ||
        p.client_company?.toLowerCase().includes(query) ||
        p.client_name?.toLowerCase().includes(query) ||
        p.location_city?.toLowerCase().includes(query)
      );
    }
    
    setFilteredProjects(filtered);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("no-NO", {
      style: "currency",
      currency: "NOK",
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending_holding_review: { 
        label: "Venter Godkjenning", 
        color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
        icon: Clock
      },
      negotiation: { 
        label: "Forhandling", 
        color: "bg-orange-500/20 text-orange-400 border-orange-500/30",
        icon: AlertTriangle
      },
      approved_pending_client_sign: { 
        label: "Venter Signatur", 
        color: "bg-blue-500/20 text-blue-400 border-blue-500/30",
        icon: Clock
      },
      live: { 
        label: "Live", 
        color: "bg-green-500/20 text-green-400 border-green-500/30",
        icon: CheckCircle
      },
      has_applications: { 
        label: "Har Søknader", 
        color: "bg-purple-500/20 text-purple-400 border-purple-500/30",
        icon: Users
      },
      has_counter_offers: { 
        label: "Har Mottilbud", 
        color: "bg-amber-500/20 text-amber-400 border-amber-500/30",
        icon: DollarSign
      },
      team_selected: { 
        label: "Team Valgt", 
        color: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
        icon: Users
      },
      team_contracted: { 
        label: "Kontrakt Signert", 
        color: "bg-teal-500/20 text-teal-400 border-teal-500/30",
        icon: CheckCircle
      },
      in_progress: { 
        label: "Pågår", 
        color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
        icon: CheckCircle
      },
      completed: { 
        label: "Fullført", 
        color: "bg-gray-500/20 text-gray-400 border-gray-500/30",
        icon: CheckCircle
      }
    };
    
    return configs[status] || { 
      label: status, 
      color: "bg-gray-500/20 text-gray-400 border-gray-500/30",
      icon: Clock 
    };
  };

  const getProjectTypeLabel = (type) => {
    return type === "workforce" ? "Workforce" : "Stillastjenester";
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6" data-testid="holding-projects">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Prosjekter</h1>
            <p className="text-gray-400 mt-1">
              Administrer alle prosjekter og søknader
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Søk etter prosjekt, klient, sted..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#0f0f1a] border-[#2a2a4a] text-white"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-[200px] bg-[#0f0f1a] border-[#2a2a4a] text-white">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                  <SelectItem value="all">Alle statuser</SelectItem>
                  <SelectItem value="pending">Venter godkjenning</SelectItem>
                  <SelectItem value="live">Live (søker team)</SelectItem>
                  <SelectItem value="active">Aktive (har team)</SelectItem>
                  <SelectItem value="completed">Fullført</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-400">
                {projects.filter(p => p.status === "pending_holding_review").length}
              </p>
              <p className="text-gray-400 text-sm">Venter godkjenning</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-400">
                {projects.filter(p => ["live", "has_applications", "has_counter_offers"].includes(p.status)).length}
              </p>
              <p className="text-gray-400 text-sm">Live</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-cyan-400">
                {projects.filter(p => ["team_selected", "team_contracted", "in_progress"].includes(p.status)).length}
              </p>
              <p className="text-gray-400 text-sm">Aktive</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#23F7DD]">
                {formatCurrency(filteredProjects.reduce((sum, p) => sum + (p.final_budget || p.proposed_budget || 0), 0))}
              </p>
              <p className="text-gray-400 text-sm">Total verdi</p>
            </CardContent>
          </Card>
        </div>

        {/* Projects List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-gray-400 mt-4">Laster prosjekter...</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardContent className="p-12 text-center">
                <AlertTriangle className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                <h3 className="text-white font-medium mb-2">Ingen prosjekter funnet</h3>
                <p className="text-gray-400">
                  {searchQuery || statusFilter !== "all" 
                    ? "Prøv å endre søk eller filter"
                    : "Det er ingen prosjekter ennå"}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredProjects.map((project) => {
              const statusConfig = getStatusConfig(project.status);
              const StatusIcon = statusConfig.icon;
              
              return (
                <Card key={project.project_id} className="bg-[#1a1a2e] border-[#2a2a4a] hover:border-[#23F7DD]/50 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* Left - Project Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg ${statusConfig.color} flex items-center justify-center flex-shrink-0`}>
                            <StatusIcon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-white font-semibold truncate">{project.title}</h3>
                              <Badge className={`${statusConfig.color} border`}>
                                {statusConfig.label}
                              </Badge>
                              <Badge variant="outline" className="border-[#2a2a4a] text-gray-400">
                                {getProjectTypeLabel(project.project_type)}
                              </Badge>
                            </div>
                            <p className="text-gray-400 text-sm mt-1">
                              {project.client_company || project.client_name}
                            </p>
                            
                            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                <span>{project.location_city}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                <span>{project.crew_size} personer</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                <span>{project.estimated_duration_days} dager</span>
                              </div>
                              {project.applications?.length > 0 && (
                                <Badge className="bg-purple-500/20 text-purple-400 border-0">
                                  {project.applications.length} søknader
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Right - Budget & Actions */}
                      <div className="flex items-center gap-4 lg:flex-col lg:items-end">
                        <div className="text-right">
                          <p className="text-[#23F7DD] font-bold text-xl">
                            {formatCurrency(project.final_budget || project.proposed_budget)}
                          </p>
                          {project.team_budget && (
                            <p className="text-gray-500 text-sm">
                              Team: {formatCurrency(project.team_budget)}
                            </p>
                          )}
                        </div>
                        <Link to={`/holding/projects/${project.project_id}`}>
                          <Button className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black">
                            <Eye className="w-4 h-4 mr-2" />
                            Se detaljer
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
